import { Box, IconButton, Image, InputArea } from "@wix/design-system";
import { usePanorama } from "@wix/fe-essentials-standalone";
import { forwardRef, useEffect } from "react";
import { useImageInputArea } from "./useImageInputArea";
import { Dismiss } from "@wix/wix-ui-icons-common";
import "./ImageInputArea.css";
import { SURFACE_SUNKEN_COLOR } from "@wix/picasso-common";
import { SUPPORTED_IMAGE_MIME_TYPES } from "../../../consts";

interface ImageInputAreaProps {
  prompt: string;
  isLoading: boolean;
  placeholder: string;
  onChange: (value: string) => void;
  handleGenerateSiteClick: () => void;
  onFileAttachment: (files: string[]) => void;
}

export const ImageInputArea = forwardRef<HTMLInputElement, ImageInputAreaProps>(
  (
    {
      placeholder,
      isLoading,
      prompt,
      onChange,
      onFileAttachment,
      handleGenerateSiteClick,
    },
    ref,
  ) => {
    const { reportLoadFinish } = usePanorama();
    const {
      handleFileAttachment,
      removeAttachment,
      onEnterPressed,
      attachmentPreviews,
      onPastePressed,
    } = useImageInputArea(onFileAttachment, handleGenerateSiteClick);

    useEffect(() => {
      reportLoadFinish();
    }, []);

    return (
      <Box direction="vertical" className="input-area-container">
        <input
          type="file"
          accept={SUPPORTED_IMAGE_MIME_TYPES.join(",")}
          multiple
          style={{ display: "none" }}
          ref={ref}
          onChange={(e) => {
            handleFileAttachment(e.target.files);
          }}
        />
        {attachmentPreviews.length > 0 && (
          <Box
            borderRadius="12px 12px 0 0"
            width="100%"
            backgroundColor={SURFACE_SUNKEN_COLOR}
            maxHeight="75px"
          >
            {attachmentPreviews.map((attachment: string, index: number) => (
              <Box
                key={index}
                paddingTop={2}
                paddingLeft={2}
                position="relative"
              >
                <Image
                  src={attachment}
                  fit="cover"
                  width="60px"
                  height="60px"
                />
                <Box
                  position="absolute"
                  right="3px"
                  top="11px"
                  width="18px"
                  height="18px"
                  backgroundColor="rgba(255, 255, 255, 0.30)"
                  borderRadius="100%"
                  display="flex"
                  align="center"
                  verticalAlign="middle"
                  overflow="hidden"
                >
                  <IconButton
                    width="18px"
                    height="18px"
                    onClick={() => removeAttachment(index)}
                    skin="transparent"
                    priority="secondary"
                    size="tiny"
                  >
                    <Dismiss scale={0.8} color="#000" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Box
          direction="vertical"
          width="100%"
          className="input-area" //TODO - Remove after design system upgrade
        >
          <InputArea>
            {() => (
              <textarea
                placeholder={placeholder}
                rows={3}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && prompt) {
                    onEnterPressed(e);
                  }
                }}
                value={prompt}
                onChange={(e) => onChange(e.target.value)}
                dir="auto"
                onPaste={(e) => {
                  if (e.clipboardData?.types.includes("Files")) {
                    e.preventDefault();
                    onPastePressed(e);
                  }
                }}
              ></textarea>
            )}
          </InputArea>
        </Box>
      </Box>
    );
  },
);

ImageInputArea.displayName = "ImageInputArea";
